import React, { useCallback, useMemo, useState, useEffect } from "react";
import moment, { Moment } from "moment-timezone";
import {
  EuiPanel,
  EuiFlexGroup,
  EuiFlexItem,
  EuiTitle,
  EuiText,
  EuiSelect,
  EuiIcon,
  EuiSpacer,
  EuiDatePicker,
} from "@elastic/eui";
import Button from "components/Buttons/Button";
import IconButton from "components/Buttons/IconButton";
import { DayCell } from "./DayCell";

export interface DatePickerProps {
  onApply: (startDate: Moment, endDate: Moment) => void;
  onCancel: (any: any) => any;
  onReset: (any: any) => any;
  initialStartDate?: Moment;
  initialEndDate?: Moment;
  quickTime: string | number;
  setQuickTime?: any;
  isSilencingWindows?: boolean;
}

interface CalendarState {
  month: number;
  year: number;
}

const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const TOTAL_CELLS = 42;

export const CustomDatePicker: React.FC<DatePickerProps> = ({
  onApply,
  onCancel,
  onReset,
  initialStartDate,
  initialEndDate,
  quickTime,
  setQuickTime,
  isSilencingWindows,
}) => {
  const now = useMemo(() => moment(), []);
  const [timeError, setTimeError] = useState(false);

  // ---------- State ----------
  const [startDate, setStartDate] = useState<Moment | null>(
    initialStartDate ? moment(initialStartDate) : null
  );

  const [endDate, setEndDate] = useState<Moment | null>(
    initialEndDate ? moment(initialEndDate) : null
  );

  // NEW: use moment instead of string for time
  const [startTime, setStartTime] = useState<Moment | null>(
    initialStartDate ? moment(initialStartDate) : null
  );
  const [endTime, setEndTime] = useState<Moment | null>(
    initialEndDate ? moment(initialEndDate) : null
  );

  const [leftCalendar, setLeftCalendar] = useState<CalendarState>(() => {
    const prev = now.clone().subtract(1, "month");
    return { month: prev.month(), year: prev.year() };
  });

  const [rightCalendar, setRightCalendar] = useState<CalendarState>(() => {
    return { month: now.month(), year: now.year() };
  });

  // ---------- Helpers ----------
  const getDaysInMonth = (month: number, year: number) => moment({ year, month }).daysInMonth();

  const getFirstDayOfMonth = (month: number, year: number) => moment({ year, month, day: 1 }).day();

  const isInRange = (date: Moment) => {
    if (!startDate || !endDate) return false;
    return date.isBetween(startDate, endDate, "day", "[]");
  };

  const isRangeStart = (date: Moment) => (startDate ? date.isSame(startDate, "day") : false);

  const isRangeEnd = (date: Moment) => (endDate ? date.isSame(endDate, "day") : false);

  const isStartAfterEnd = (startTime: any, endTime: any) => {
    const startMinutes = startTime.hour() * 60 + startTime.minute();
    const endMinutes = endTime.hour() * 60 + endTime.minute();
    return startMinutes > endMinutes;
  };

  const validateTime = (
    startD: Moment | null,
    endD: Moment | null,
    startT: Moment | null,
    endT: Moment | null
  ) => {
    if (!startT || !endT) return false;

    // single-day range
    if (!endD) {
      return isStartAfterEnd(startT, endT);
    }

    // same-day range
    if (startD && endD && startD.isSame(endD, "day")) {
      return isStartAfterEnd(startT, endT);
    }

    return false;
  };

  const handleDateClick = (day: number, month: number, year: number) => {
    setQuickTime("");
    const clicked = moment({ year, month, day });

    if (!startDate || (startDate && endDate)) {
      setStartDate(clicked);
      setEndDate(null);
      return;
    }

    if (clicked.isBefore(startDate, "day")) {
      setStartDate(clicked);
      setEndDate(null);
      return;
    }

    setEndDate(clicked);
  };

  const navigateMonth = (direction: "prev" | "next", calendar: "left" | "right") => {
    const current = calendar === "left" ? leftCalendar : rightCalendar;

    const updatedMoment = moment({ year: current.year, month: current.month }).add(
      direction === "next" ? 1 : -1,
      "month"
    );

    const newState: CalendarState = {
      month: updatedMoment.month(),
      year: updatedMoment.year(),
    };

    if (calendar === "left") {
      setLeftCalendar(newState);
      const partner = updatedMoment.clone().add(1, "month");
      setRightCalendar({ month: partner.month(), year: partner.year() });
    } else {
      setRightCalendar(newState);
      const partner = updatedMoment.clone().subtract(1, "month");
      setLeftCalendar({ month: partner.month(), year: partner.year() });
    }
  };

  // ---------- Render calendar ----------
  const renderCalendar = (calendarState: CalendarState, isLeft: boolean) => {
    const { month, year } = calendarState;
    const daysInMonth = getDaysInMonth(month, year);
    const firstDay = getFirstDayOfMonth(month, year);

    const prev = moment({ year, month }).subtract(1, "month");
    const next = moment({ year, month }).add(1, "month");

    const cells: React.ReactNode[] = [];

    // Prev month
    const prevDays = prev.daysInMonth();
    for (let i = firstDay - 1; i >= 0; i--) {
      const d = prevDays - i;
      const date = prev.clone().date(d);
      cells.push(
        <DayCell
          key={`prev-${d}`}
          date={date}
          month={prev.month()}
          year={prev.year()}
          onClick={() => handleDateClick(d, prev.month(), prev.year())}
          isStart={isRangeStart(date)}
          isEnd={isRangeEnd(date)}
          inRange={isInRange(date)}
          isToday={date.isSame(moment(), "day")}
          muted
        />
      );
    }

    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      const date = moment({ year, month, day: d });
      cells.push(
        <DayCell
          key={`cur-${d}`}
          date={date}
          month={month}
          year={year}
          onClick={() => handleDateClick(d, month, year)}
          isStart={isRangeStart(date)}
          isEnd={isRangeEnd(date)}
          inRange={isInRange(date)}
          isToday={date.isSame(moment(), "day")}
          muted={false}
        />
      );
    }

    // Next month
    const remaining = TOTAL_CELLS - cells.length;
    for (let d = 1; d <= remaining; d++) {
      const date = next.clone().date(d);
      cells.push(
        <DayCell
          key={`next-${d}`}
          date={date}
          month={next.month()}
          year={next.year()}
          onClick={() => handleDateClick(d, next.month(), next.year())}
          isStart={isRangeStart(date)}
          isEnd={isRangeEnd(date)}
          inRange={isInRange(date)}
          isToday={date.isSame(moment(), "day")}
          muted
        />
      );
    }

    useEffect(() => {
      const hasError = validateTime(startDate, endDate, startTime, endTime);
      setTimeError(hasError);
    }, [startDate, endDate, startTime, endTime]);

    return (
      <EuiFlexItem style={{ minWidth: 0 }}>
        <EuiPanel
          hasShadow={false}
          paddingSize="s"
          style={{ border: "none", backgroundColor: "var(--card-background)" }}>
          <EuiFlexGroup
            alignItems="center"
            justifyContent="center"
            gutterSize="none"
            responsive={false}>
            <EuiFlexItem grow={false}>
              <IconButton
                type="arrowLeft"
                color="var(--text)"
                onClick={() => navigateMonth("prev", "left")}
                size="s"
              />
            </EuiFlexItem>

            <EuiFlexItem style={{ textAlign: "center", minWidth: 0 }}>
              <EuiTitle size="xs">
                <h3 style={{ margin: 0, fontWeight: 800 }}>
                  {moment({ year, month }).format("MMMM YYYY")}
                </h3>
              </EuiTitle>
            </EuiFlexItem>

            <EuiFlexItem style={{ textAlign: "right" }} grow={false}>
              {!isLeft && (
                <IconButton
                  color="var(--text)"
                  type="arrowRight"
                  onClick={() => navigateMonth("next", "right")}
                  size="s"
                />
              )}
            </EuiFlexItem>
          </EuiFlexGroup>

          <EuiSpacer size="l" />

          {/* Day names */}
          <EuiFlexGroup
            gutterSize="s"
            alignItems="center"
            responsive={false}
            style={{
              marginBottom: 6,
              paddingBottom: 6,
              borderBottom: "1px solid var(--border-color)",
            }}>
            {DAY_NAMES.map((name) => (
              <EuiFlexItem key={name} style={{ textAlign: "center" }}>
                <EuiText size="s" style={{ fontWeight: 600, color: "var(--text)" }}>
                  {name}
                </EuiText>
              </EuiFlexItem>
            ))}
          </EuiFlexGroup>

          {/* Day grid */}
          <div
            style={{
              display: "grid",
              paddingTop: 4,
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 8,
              justifyItems: "center",
              alignItems: "center",
            }}>
            {cells}
          </div>
        </EuiPanel>
      </EuiFlexItem>
    );
  };

  // ---------- Apply ----------
  const handleApply = useCallback(() => {
    if (!startDate) return;

    const s = startDate.clone();
    const e = endDate ? endDate.clone() : startDate.clone();

    if (startTime) {
      s.hour(startTime.hour()).minute(startTime.minute()).second(0);
    }
    if (endTime) {
      e.hour(endTime.hour()).minute(endTime.minute()).second(0);
    }

    onApply(s, e);
  }, [startDate, endDate, startTime, endTime, onApply]);

  return (
    <EuiPanel
      paddingSize="m"
      style={{
        maxWidth: 920,
        margin: "0 auto",
        borderRadius: 8,
        backgroundColor: "var(--card-background)",
        position: "relative",
        zIndex: 99999,
      }}>
      <EuiFlexGroup direction="column" gutterSize="m">
        {/* Calendar */}
        <EuiFlexItem>
          <EuiFlexGroup responsive={false} gutterSize="l" alignItems="stretch">
            {renderCalendar(rightCalendar, false)}
          </EuiFlexGroup>
        </EuiFlexItem>
        {/* Quick select */}
        <div style={{ paddingTop: 16, marginTop: "-24px" }}>
          <EuiFlexItem grow={false}>
            <EuiText size="s">
              <label style={{ fontWeight: 600, paddingBottom: 4, display: "block" }}>
                Quick Select
              </label>
            </EuiText>

            <div style={{ position: "relative" }}>
              <EuiIcon
                type="clock"
                size="m"
                style={{
                  position: "absolute",
                  left: 12,
                  top: 20,
                  transform: "translateY(-50%)",
                  color: "var(--text)",
                  zIndex: 99,
                }}
              />

              <EuiSelect
                value={quickTime}
                onChange={(e) => {
                  setQuickTime(e.target.value);
                  if (isSilencingWindows) {
                    setStartDate(moment().subtract(e.target.value, "days"));
                    setEndDate(moment().add(e.target.value, "days"));
                    setStartTime(moment());
                    setEndTime(moment());
                  } else {
                    setStartDate(moment().subtract(e.target.value, "hours"));
                    setEndDate(moment());
                    setStartTime(moment().subtract(e.target.value, "hours"));
                    setEndTime(moment());
                  }
                }}
                options={
                  isSilencingWindows
                    ? [
                        { value: "", text: "Select ...", disabled: true },
                        { text: "Past 30 days - Future 30 days", value: 30 },
                        { text: "Past 60 days - Future 60 days", value: 60 },
                      ]
                    : [
                        { value: "", text: "Select ...", disabled: true },
                        { text: "Last 2 hours", value: 2 },
                        { text: "Last 4 hours", value: 4 },
                        { text: "Last 24 hours", value: 24 },
                        { text: "Last 48 hours", value: 48 },
                        { text: "Last 7 days", value: 168 },
                        { text: "Last 14 days", value: 336 },
                      ]
                }
                compressed
                style={{ paddingLeft: 40, height: "40px" }}
              />
            </div>
          </EuiFlexItem>
        </div>

        {/* Time pickers */}
        <EuiFlexItem>
          <div style={{ paddingTop: 16 }}>
            <EuiFlexGroup responsive={false} gutterSize="m">
              {/* Start time */}
              <EuiFlexItem style={{ width: "160px" }}>
                <EuiText size="s">
                  <label style={{ fontWeight: 600, paddingBottom: 4, display: "block" }}>
                    Start Time
                  </label>
                </EuiText>

                <div onMouseDownCapture={(e) => e.stopPropagation()}>
                  <EuiDatePicker
                    selected={startTime}
                    onChange={(val) => {
                      if (!val) return;
                      const updated = startDate
                        ? startDate.clone().hour(val.hour()).minute(val.minute())
                        : val;

                      setStartTime(updated);
                      setQuickTime("");
                    }}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={30}
                    dateFormat="HH:mm"
                    timeFormat="HH:mm"
                    placeholder="Select start time"
                    aria-label="Start time"
                  />
                </div>
              </EuiFlexItem>

              {/* End time */}
              <EuiFlexItem style={{ width: "160px" }}>
                <EuiText size="s">
                  <label style={{ fontWeight: 600, paddingBottom: 4, display: "block" }}>
                    End Time
                  </label>
                </EuiText>
                <div onMouseDownCapture={(e) => e.stopPropagation()}>
                  <EuiDatePicker
                    selected={endTime}
                    onChange={(val) => {
                      if (!val) return;

                      const updated = endDate
                        ? endDate.clone().hour(val.hour()).minute(val.minute())
                        : val;

                      setEndTime(updated);
                      setQuickTime("");
                    }}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={30}
                    timeFormat="HH:mm"
                    dateFormat="HH:mm"
                    placeholder="Select end time"
                    aria-label="End time"
                  />
                </div>
              </EuiFlexItem>
            </EuiFlexGroup>

            {timeError && (
              <div
                style={{
                  marginTop: 16,
                  borderRadius: 6,
                  padding: 12,
                  border: "1px solid var(--border-color)",
                  color: "#ed494a",
                  width: "336px",
                  overflowWrap: "break-word",
                  whiteSpace: "wrap",
                }}>
                Start time must be earlier than the end time for the same day.
              </div>
            )}
          </div>
        </EuiFlexItem>

        {/* Buttons */}
        <EuiFlexItem>
          <EuiFlexGroup justifyContent="spaceBetween" responsive={false}>
            <EuiFlexItem grow={false}>
              <Button text="Reset" type="empty" onClick={onReset} />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiFlexGroup justifyContent="flexEnd" responsive={false}>
                <EuiFlexItem grow={false}>
                  <Button text="Cancel" type="secondary" onClick={onCancel} />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <Button text="Apply" type="primary" onClick={handleApply} disabled={timeError} />
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiPanel>
  );
};
